import { landingVariants } from "../content/landing/registry";
import type { LandingSection, LandingVariant } from "../content/landing/types";

export const PRODUCTION_ORIGIN = "https://revisionlab.ai";

export type RouteView =
  | "RootView"
  | "HomeView"
  | "LoginView"
  | "RegisterView"
  | "VerifyView"
  | "ClaimView"
  | "FundView"
  | "ResetPasswordView"
  | "SubjectView"
  | "AccountView"
  | "PhoneCaptureView"
  | "PrivacyView"
  | "TermsView"
  | "AdminDashboard"
  | "AdminCourseDetail"
  | "AdminProductStats"
  | "PurchaseSuccessView";

export type RouteMetaContract = {
  attributionSurface?: boolean;
  variantSlug?: string;
  guestOnly?: boolean;
  requiresAuth?: boolean;
  requiresEntitlement?: boolean;
  requiresPermission?: string;
};

type ViewRouteDescriptor = {
  kind: "view";
  path: string;
  name: string;
  view: RouteView;
  props?: boolean | Readonly<Record<string, string>>;
  meta?: RouteMetaContract;
  prerender?: "head" | "full";
  indexable?: boolean;
  inSitemap?: boolean;
  canonical?: `https://${string}`;
};

export type RedirectContract =
  | { kind: "static"; to: string }
  | { kind: "subject"; suffix?: "?session=1" };

type RedirectRouteDescriptor = {
  kind: "redirect";
  path: string;
  redirect: RedirectContract;
};

export type RouteDescriptor = ViewRouteDescriptor | RedirectRouteDescriptor;

const staticRouteManifest: readonly RouteDescriptor[] = [
  {
    kind: "view",
    path: "/login",
    name: "login",
    view: "LoginView",
    meta: { guestOnly: true },
  },
  {
    kind: "view",
    path: "/register",
    name: "register",
    view: "RegisterView",
    meta: { guestOnly: true, attributionSurface: true },
  },
  {
    kind: "view",
    path: "/verify",
    name: "verify",
    view: "VerifyView",
  },
  // Claim and funding links deliberately stay public after sign-in. Each view
  // reads then strips its optional token; route props would retain a second
  // copy, while guestOnly would eject the visitor mid-journey.
  {
    kind: "view",
    path: "/claim/:token?",
    name: "claim",
    view: "ClaimView",
  },
  {
    kind: "view",
    path: "/fund/:token?",
    name: "fund",
    view: "FundView",
  },
  {
    kind: "view",
    path: "/reset-password",
    name: "reset-password",
    view: "ResetPasswordView",
    meta: { guestOnly: true },
  },
  {
    kind: "view",
    path: "/subjects/:subjectId",
    name: "subject",
    view: "SubjectView",
    props: true,
    meta: { requiresAuth: true, requiresEntitlement: true },
  },
  {
    kind: "view",
    path: "/account",
    name: "account",
    view: "AccountView",
    meta: { requiresAuth: true },
  },
  {
    kind: "view",
    path: "/phone-capture/:token?",
    name: "phone-capture",
    view: "PhoneCaptureView",
    props: true,
  },
  {
    kind: "view",
    path: "/privacy",
    name: "privacy",
    view: "PrivacyView",
    prerender: "full",
    indexable: true,
    inSitemap: true,
    canonical: "https://revisionlab.ai/privacy",
  },
  {
    kind: "view",
    path: "/terms",
    name: "terms",
    view: "TermsView",
    prerender: "full",
    indexable: true,
    inSitemap: true,
    canonical: "https://revisionlab.ai/terms",
  },
  {
    kind: "view",
    path: "/admin",
    name: "admin",
    view: "AdminDashboard",
    meta: {
      requiresAuth: true,
      requiresPermission: "admin.catalogue.read",
    },
  },
  {
    kind: "view",
    path: "/admin/subjects/:subjectId",
    name: "admin-subject",
    view: "AdminCourseDetail",
    props: true,
    meta: {
      requiresAuth: true,
      requiresPermission: "admin.catalogue.read",
    },
  },
  {
    kind: "view",
    path: "/admin/product-stats",
    name: "admin-product-stats",
    view: "AdminProductStats",
    meta: {
      requiresAuth: true,
      requiresPermission: "admin.usage.read",
    },
  },
  // Legacy URLs remain live in sent email and browser history. Keep them in
  // the same manifest so Pages deep-link coverage cannot omit them.
  { kind: "redirect", path: "/home", redirect: { kind: "static", to: "/" } },
  {
    kind: "redirect",
    path: "/catalogue",
    redirect: { kind: "static", to: "/?add=1" },
  },
  {
    kind: "redirect",
    path: "/subjects/:subjectId/progress",
    redirect: { kind: "subject" },
  },
  {
    kind: "redirect",
    path: "/subjects/:subjectId/profile",
    redirect: { kind: "subject" },
  },
  {
    kind: "redirect",
    path: "/subjects/:subjectId/session",
    redirect: { kind: "subject", suffix: "?session=1" },
  },
  {
    kind: "redirect",
    path: "/purchase/cancel",
    redirect: { kind: "static", to: "/" },
  },
  {
    kind: "redirect",
    path: "/subscribe/cancel",
    redirect: { kind: "static", to: "/?add=1" },
  },
  // Checkout success is a real confirmation view; only the retired
  // /purchase/success spelling redirects to it.
  {
    kind: "redirect",
    path: "/purchase/success",
    redirect: { kind: "static", to: "/subscribe/success" },
  },
  {
    kind: "view",
    path: "/subscribe/success",
    name: "purchase-success",
    view: "PurchaseSuccessView",
    meta: { requiresAuth: true },
  },
];

function variantDescriptor(variant: LandingVariant): RouteDescriptor {
  if (variant.publication.status === "retired") {
    return {
      kind: "redirect",
      path: variant.path,
      redirect: { kind: "static", to: variant.publication.redirectTo },
    };
  }

  return {
    kind: "view",
    path: variant.path,
    name: variant.slug === "default" ? "root" : `landing-${variant.slug}`,
    view: variant.slug === "default" ? "RootView" : "HomeView",
    ...(variant.slug === "default" ? {} : { props: { variantSlug: variant.slug } }),
    meta: { attributionSurface: true, variantSlug: variant.slug },
    prerender: variant.slug === "default" ? "head" : "full",
    indexable: variant.publication.indexable,
    inSitemap: variant.publication.inSitemap,
    canonical: variant.seo.canonical,
  };
}

export function createRouteManifest(
  variants: readonly LandingVariant[] = landingVariants,
): readonly RouteDescriptor[] {
  return [...variants.map(variantDescriptor), ...staticRouteManifest];
}

export const routeManifest = createRouteManifest();

export function createPrerenderAllowlist(
  manifest: readonly RouteDescriptor[] = routeManifest,
) {
  return manifest.flatMap((route) =>
    route.kind === "view" && route.prerender !== undefined
      ? [{ path: route.path, mode: route.prerender }]
      : [],
  );
}

export const prerenderAllowlist = createPrerenderAllowlist();

export function createSitemapEntries(
  manifest: readonly RouteDescriptor[] = routeManifest,
) {
  return manifest.flatMap((route) =>
    route.kind === "view" &&
    route.inSitemap === true &&
    route.canonical !== undefined
      ? [{ path: route.path, canonical: route.canonical }]
      : [],
  );
}

export const sitemapEntries = createSitemapEntries();

function hostingPatterns(path: string): readonly string[] {
  const optional = path.match(/^(.*)\/:([^/]+)\?$/);
  if (optional !== null) return [optional[1] || "/", `${optional[1]}/*`];
  return [path.replace(/:[^/]+/g, "*")];
}

export function createSpaAllowlist(
  manifest: readonly RouteDescriptor[] = routeManifest,
) {
  return manifest.flatMap((route) =>
    route.path === "/" ? [] : hostingPatterns(route.path),
  );
}

export const spaAllowlist = createSpaAllowlist();

const SECTION_ANCHORS: Readonly<Record<LandingSection["kind"], readonly string[]>> = {
  hero: [],
  boardStrip: [],
  productPreview: ["sample"],
  howItWorks: ["how-it-works"],
  pillars: [],
  studyOptions: [],
  pricing: ["pricing"],
  focus: ["student-focus"],
  quotes: [],
  comparison: [],
  faq: ["faq"],
  closing: [],
  story: [],
};

function routePatternMatchesPath(pattern: string, path: string): boolean {
  const patternSegments = pattern.split("/").filter(Boolean);
  const pathSegments = path.split("/").filter(Boolean);
  let pathIndex = 0;

  for (const segment of patternSegments) {
    const dynamic = segment.startsWith(":");
    const optional = dynamic && segment.endsWith("?");
    if (pathIndex >= pathSegments.length) {
      if (optional) continue;
      return false;
    }
    if (!dynamic && segment !== pathSegments[pathIndex]) return false;
    pathIndex += 1;
  }

  return pathIndex === pathSegments.length;
}

function validateVariant(
  variant: LandingVariant,
  reservedPaths: readonly string[],
  knownPaths: ReadonlySet<string>,
  assetExists?: (publicPath: string) => boolean,
): string[] {
  const errors: string[] = [];
  const label = `variant ${variant.slug}`;

  if (
    variant.slug !== "default" &&
    reservedPaths.some((pattern) => routePatternMatchesPath(pattern, variant.path))
  ) {
    errors.push(`${label}: path ${variant.path} collides with an app route`);
  }
  if (
    variant.path.includes(":") ||
    variant.path.includes("?") ||
    variant.path.includes("#") ||
    (variant.path !== "/" && variant.path.endsWith("/"))
  ) {
    errors.push(`${label}: path must be a static, canonical route path`);
  }

  let canonical: URL | undefined;
  try {
    canonical = new URL(variant.seo.canonical);
  } catch {
    errors.push(`${label}: canonical must be an absolute URL`);
  }
  if (canonical !== undefined && canonical.origin !== PRODUCTION_ORIGIN) {
    errors.push(`${label}: canonical must use ${PRODUCTION_ORIGIN}`);
  }
  if (
    canonical !== undefined &&
    (canonical.search !== "" || canonical.hash !== "")
  ) {
    errors.push(`${label}: canonical cannot contain a query string or fragment`);
  }

  if (variant.publication.status === "live") {
    if (variant.publication.inSitemap && !variant.publication.indexable) {
      errors.push(`${label}: an in-sitemap variant must be indexable`);
    }
    if (
      variant.publication.inSitemap &&
      canonical !== undefined &&
      canonical.pathname !== variant.path
    ) {
      errors.push(`${label}: an in-sitemap variant must be self-canonical`);
    }
  } else {
    const redirectPath = variant.publication.redirectTo.split(/[?#]/, 1)[0] || "/";
    if (variant.publication.redirectTo === variant.path) {
      errors.push(`${label}: a retired variant cannot redirect to itself`);
    }
    if (!knownPaths.has(redirectPath)) {
      errors.push(`${label}: retired redirect target does not exist: ${redirectPath}`);
    }
  }

  if (assetExists !== undefined && !assetExists(variant.seo.ogImage)) {
    errors.push(`${label}: OG image does not exist: ${variant.seo.ogImage}`);
  }

  const anchors = new Set<string>();
  let heroCount = 0;
  const collectAnchors = (sections: readonly LandingSection[]): void => {
    for (const section of sections) {
      if (!(section.kind in SECTION_ANCHORS)) {
        errors.push(`${label}: unresolved section kind: ${String(section.kind)}`);
        continue;
      }
      if (section.kind === "hero") heroCount += 1;
      for (const anchor of SECTION_ANCHORS[section.kind]) anchors.add(anchor);
    }
  };
  collectAnchors(variant.sections);
  if (heroCount !== 1) errors.push(`${label}: exactly one hero section is required`);


  const visit = (value: unknown, key?: string): void => {
    if (typeof value === "string" && (key === "to" || key === "href")) {
      if (value.startsWith("#") && !anchors.has(value.slice(1))) {
        errors.push(`${label}: unresolved in-page CTA target: ${value}`);
      }
      if (
        !value.startsWith("/") &&
        !value.startsWith("#") &&
        !value.startsWith("https://")
      ) {
        errors.push(`${label}: CTA target must be internal, an anchor or HTTPS: ${value}`);
      }
      // Nav links name pages (GitHub #325); a typo there is a dead link on
      // every page, so an internal target must be a route this app serves.
      if (value.startsWith("/")) {
        const path = value.split(/[?#]/, 1)[0] || "/";
        if (![...knownPaths].some((pattern) => routePatternMatchesPath(pattern, path))) {
          errors.push(`${label}: internal link target is not a route: ${value}`);
        }
      }
      return;
    }
    if (Array.isArray(value)) {
      for (const item of value) visit(item);
      return;
    }
    if (value !== null && typeof value === "object") {
      for (const [childKey, child] of Object.entries(value)) visit(child, childKey);
    }
  };
  visit(variant);

  return errors;
}

export function validateRouteManifest(options: {
  assetExists?: (publicPath: string) => boolean;
  variants?: readonly LandingVariant[];
} = {}): void {
  const variants = options.variants ?? landingVariants;
  const reservedPaths = staticRouteManifest.map((route) => route.path);
  const knownPaths = new Set([
    ...reservedPaths,
    ...variants.map((variant) => variant.path),
  ]);
  const errors: string[] = [];
  const slugs = new Set<string>();
  const paths = new Set<string>();

  for (const variant of variants) {
    if (slugs.has(variant.slug)) errors.push(`duplicate variant slug: ${variant.slug}`);
    if (paths.has(variant.path)) errors.push(`duplicate variant path: ${variant.path}`);
    slugs.add(variant.slug);
    paths.add(variant.path);
    errors.push(
      ...validateVariant(
        variant,
        reservedPaths,
        knownPaths,
        options.assetExists,
      ),
    );
  }

  const defaultVariant = variants.filter((variant) => variant.slug === "default");
  if (defaultVariant.length !== 1 || defaultVariant[0]?.path !== "/") {
    errors.push('exactly one "default" variant at / is required');
  }

  if (errors.length > 0) {
    throw new Error(`Invalid route manifest:\n- ${errors.join("\n- ")}`);
  }
}
